import React, { useEffect, useRef, useState } from 'react';

// items: [{ key, label, onSelect, destructive?, disabled? }]
function TileMenu({ items, isOpen, onOpen, onClose }) {
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const itemRefs = useRef([]);
  const [activeIndex, setActiveIndex] = useState(-1);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsideClick = (e) => {
      if (
        buttonRef.current?.contains(e.target) ||
        menuRef.current?.contains(e.target)
      ) {
        return;
      }
      onClose();
    };

    const handleScroll = () => onClose();

    document.addEventListener('mousedown', handleOutsideClick);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setActiveIndex(-1);
      return;
    }
    const firstEnabled = items.findIndex((item) => !item.disabled);
    if (firstEnabled !== -1) {
      setActiveIndex(firstEnabled);
      itemRefs.current[firstEnabled]?.focus();
    }
    // Re-run only when open state changes, not on every items re-creation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const enabledIndexes = items.reduce((acc, item, index) => {
    if (!item.disabled) acc.push(index);
    return acc;
  }, []);

  const moveFocus = (direction) => {
    if (enabledIndexes.length === 0) return;
    const currentPos = enabledIndexes.indexOf(activeIndex);
    const nextPos =
      currentPos === -1
        ? 0
        : (currentPos + direction + enabledIndexes.length) % enabledIndexes.length;
    const nextIndex = enabledIndexes[nextPos];
    setActiveIndex(nextIndex);
    itemRefs.current[nextIndex]?.focus();
  };

  const closeAndReturnFocus = () => {
    onClose();
    buttonRef.current?.focus();
  };

  const handleButtonClick = (e) => {
    e.stopPropagation();
    if (isOpen) {
      onClose();
    } else {
      onOpen();
    }
  };

  const handleButtonKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!isOpen) onOpen();
    } else if (e.key === 'Escape' && isOpen) {
      e.preventDefault();
      closeAndReturnFocus();
    }
  };

  const activateItem = (item) => {
    if (item.disabled) return;
    onClose();
    item.onSelect();
  };

  const handleItemClick = (item, e) => {
    e.stopPropagation();
    activateItem(item);
  };

  const handleItemKeyDown = (item, e) => {
    e.stopPropagation();
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      activateItem(item);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveFocus(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveFocus(-1);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeAndReturnFocus();
    }
  };

  return (
    <div className="tile-menu">
      <button
        ref={buttonRef}
        type="button"
        className="tile-menu-button"
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label="Options"
        onClick={handleButtonClick}
        onKeyDown={handleButtonKeyDown}
      >
        <span aria-hidden="true">⋯</span>
      </button>

      {isOpen && (
        <ul className="tile-menu-list" role="menu" ref={menuRef}>
          {items.map((item, index) => (
            <li
              key={item.key}
              ref={(el) => (itemRefs.current[index] = el)}
              role="menuitem"
              tabIndex={-1}
              aria-disabled={item.disabled || undefined}
              className={[
                'tile-menu-item',
                item.destructive ? 'tile-menu-item-destructive' : '',
                item.disabled ? 'tile-menu-item-disabled' : ''
              ].filter(Boolean).join(' ')}
              onClick={(e) => handleItemClick(item, e)}
              onKeyDown={(e) => handleItemKeyDown(item, e)}
            >
              {item.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default TileMenu;
